import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';
import { notes } from '../api';

/**
 * Where this device stands on push notifications, and how to turn them on.
 *
 * state:
 *   'granted'       on
 *   'default'       off, never asked -> can be turned on with one tap
 *   'denied'        blocked in the device's settings -> only the user can unblock it
 *   'needs-install' iPhone/iPad in a Safari tab: web push only works once CoachPad is on the Home Screen
 *   'ios-too-old'   iOS older than 16.4 has no web push at all
 *   'unsupported'   this browser can't do push
 */
export async function getPushStatus() {
  const ua = navigator.userAgent || '';
  const ios = /iphone|ipad|ipod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
  const android = /android/i.test(ua);

  if (Capacitor.isNativePlatform()) {
    try {
      const p = await PushNotifications.checkPermissions();
      const state = p.receive === 'granted' ? 'granted' : p.receive === 'denied' ? 'denied' : 'default';
      return { kind: 'native', ios: false, android: true, standalone: true, state };
    } catch (e) {
      return { kind: 'native', ios: false, android: true, standalone: true, state: 'unsupported' };
    }
  }

  // On iOS the Push API only exists for a web app opened from the Home Screen
  if (ios && !standalone) return { kind: 'web', ios, android, standalone, state: 'needs-install' };

  const supported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  if (!supported) return { kind: 'web', ios, android, standalone, state: ios ? 'ios-too-old' : 'unsupported' };

  const permission = Notification.permission; // 'default' | 'granted' | 'denied'
  return { kind: 'web', ios, android, standalone, state: permission };
}

function ensureFirebaseApp() {
  if (getApps().length > 0) return;
  initializeApp({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
  });
}

/**
 * Ask permission (must be called from a tap) and register this device with the backend.
 * Returns the resulting permission: 'granted' | 'denied' | 'default'.
 */
export async function enablePush() {
  if (Capacitor.isNativePlatform()) {
    const result = await PushNotifications.requestPermissions();
    if (result.receive !== 'granted') return result.receive === 'denied' ? 'denied' : 'default';
    PushNotifications.addListener('registration', async (token) => {
      try { await notes.saveFcmToken(token.value); } catch (e) { /* retried next open */ }
    });
    await PushNotifications.register();
    return 'granted';
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission;

  ensureFirebaseApp();
  const registration =
    (await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')) ||
    (await navigator.serviceWorker.register('/firebase-messaging-sw.js'));
  const token = await getToken(getMessaging(), {
    vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: registration
  });
  if (token) {
    window.fcmToken = token;
    await notes.saveFcmToken(token);
  }
  return permission;
}
